import moment from 'moment'
import { defineStore } from 'pinia'
import { readonly, ref } from 'vue'

import { useSystemStore } from '@/client/store/useSystemStore'
import {
  DEFAULT_PAGE_SIZE,
  GetTwitterBenchmarkCampaignPostsQuery,
  PostDetailResponse,
  PostListResponse,
  SearchFilters,
  SortKey
} from '@/typings/twitter_benchmark_campaign_posts'

import { getTwBenchmarkPostById } from '../api/getTwBenchmarkPostById'
import { getTwBenchmarkPosts } from '../api/getTwBenchmarkPosts'
import { createDefaultDateRange, type Order, type PageSize, type TwBenchmarkListState } from '../utils/queryParams'

type DateRange = { startDatetime: string; endDatetime: string }

/**
 * ISO8601 形式の文字列を、API 送信用の ISO8601 形式に変換する。
 */
function toIsoDatetime(value: string): string {
  if (!value) return value
  return moment(value).format()
}

/**
 * X 競合投稿一覧ストア。
 */
export const useTwBenchmarkPostsStore = defineStore('twBenchmarkPostsStore', () => {
  const posts = ref<PostListResponse['posts']>([])
  const totalCount = ref(0)
  const searchFilters = ref<SearchFilters>(createDefaultDateRange())
  const sortBy = ref<SortKey>('campaignStartDatetime')
  const order = ref<Order>('desc')
  const currentPage = ref(1)
  const pageSize = ref<PageSize>(DEFAULT_PAGE_SIZE)
  const loading = ref(false)
  const error = ref(false)
  const detail = ref<PostDetailResponse['post'] | null>(null)
  const detailLoading = ref(false)

  const systemStore = useSystemStore()

  const fetchPosts = async (): Promise<void> => {
    if (loading.value) return

    const groupId = systemStore.currentGroupId
    if (typeof groupId !== 'number') return

    loading.value = true
    error.value = false

    try {
      const query: GetTwitterBenchmarkCampaignPostsQuery = {
        groupId,
        ...searchFilters.value,
        startDatetime: toIsoDatetime(searchFilters.value.startDatetime),
        endDatetime: toIsoDatetime(searchFilters.value.endDatetime),
        sortBy: sortBy.value,
        order: order.value,
        limit: pageSize.value,
        offset: (currentPage.value - 1) * pageSize.value
      }

      const result = await getTwBenchmarkPosts(query)

      if (result.isSuccess && result.data) {
        posts.value = result.data.posts
        totalCount.value = result.data.totalCount
      } else {
        error.value = true
      }
    } finally {
      loading.value = false
    }
  }

  /**
   * URL クエリから復元した一覧 state を一括反映する（fetch は呼び出し側が hydrate 後に一度だけ実行）。
   */
  const hydrateFromState = (state: TwBenchmarkListState): void => {
    searchFilters.value = state.filters
    sortBy.value = state.sortBy
    order.value = state.order
    currentPage.value = state.page
    pageSize.value = state.pageSize
  }

  const applyFilters = async (snapshot: SearchFilters): Promise<void> => {
    searchFilters.value = snapshot
    currentPage.value = 1
    await fetchPosts()
  }

  const clearFilters = async (): Promise<void> => {
    searchFilters.value = {
      startDatetime: searchFilters.value.startDatetime,
      endDatetime: searchFilters.value.endDatetime
    }
    currentPage.value = 1
    await fetchPosts()
  }

  const setDateRange = async (range: DateRange): Promise<void> => {
    searchFilters.value = {
      ...searchFilters.value,
      startDatetime: range.startDatetime,
      endDatetime: range.endDatetime
    }
    currentPage.value = 1
    await fetchPosts()
  }

  const setSort = async (nextSortBy: SortKey, nextOrder: Order): Promise<void> => {
    sortBy.value = nextSortBy
    order.value = nextOrder
    currentPage.value = 1
    await fetchPosts()
  }

  const setPage = async (page: number): Promise<void> => {
    currentPage.value = page
    await fetchPosts()
  }

  const setPageSize = async (size: PageSize): Promise<void> => {
    pageSize.value = size
    currentPage.value = 1
    await fetchPosts()
  }

  const refresh = async (): Promise<void> => {
    await fetchPosts()
  }

  const fetchPostDetail = async (postId: number): Promise<void> => {
    const groupId = systemStore.currentGroupId
    if (typeof groupId !== 'number') {
      detail.value = null
      return
    }

    detailLoading.value = true

    try {
      const result = await getTwBenchmarkPostById(postId, groupId)

      if (result.isSuccess && result.data) {
        detail.value = result.data.post
      } else {
        detail.value = null
      }
    } finally {
      detailLoading.value = false
    }
  }

  return {
    posts: readonly(posts),
    totalCount: readonly(totalCount),
    searchFilters: readonly(searchFilters),
    sortBy: readonly(sortBy),
    order: readonly(order),
    currentPage: readonly(currentPage),
    pageSize: readonly(pageSize),
    loading: readonly(loading),
    error: readonly(error),
    detail: readonly(detail),
    detailLoading: readonly(detailLoading),

    fetchPosts,
    hydrateFromState,
    applyFilters,
    clearFilters,
    setDateRange,
    setSort,
    setPage,
    setPageSize,
    refresh,
    fetchPostDetail
  }
})
